/**
 * One freshness rule for the session caches that outlive the overnight landing.
 *
 * A tab can stay open across the landing (a laptop asleep overnight does not even
 * trip the inactivity logout), and the caches below never expire by themselves,
 * so the report date defaults, the sidebar's "Data as of", the Portfolios tab and
 * the cached client / Manual / Market Place pages would stay on the pre-landing
 * day until a full reload. Each cache module exports one refresher; ONE watcher
 * (one timer) calls them all.
 *
 * MAX_AGE_MS = 15 min: the picker already trails the lake by 1-2 h (snapshot
 * adoption), so this adds a fraction of that, while staying above the backend's
 * 5 min query memo and costing a visible tab at most four reads of each loaded
 * cache an hour.
 */
export const MAX_AGE_MS = 15 * 60 * 1000;
const CHECK_MS = 60 * 1000;

/** True once something read at `readAt` (ms epoch) is MAX_AGE_MS old. */
export const isStale = (readAt) => Date.now() - readAt >= MAX_AGE_MS;

// Bumped whenever a background refresh replaces a cache, so a mounted page that
// rendered from one (the Portfolios tab) can re-read it.
let refreshVersion = 0;
const refreshListeners = new Set();

/** Subscribe to background refreshes (for useSyncExternalStore). Returns the unsubscribe. */
export function subscribeCacheRefresh(listener) {
  refreshListeners.add(listener);
  return () => refreshListeners.delete(listener);
}

/** Count of background refreshes so far; the useSyncExternalStore snapshot. */
export function getCacheRefreshVersion() {
  return refreshVersion;
}

/**
 * Re-read one cache when it is loaded, idle and stale, and announce it when the
 * new data lands. A failed refresh keeps the old data and its old age, so the
 * next check retries. `reload` takes `{ force, fetcher }` like the loaders.
 */
export function refreshIfStale({ loaded, busy, readAt, reload }, fetcher) {
  if (!loaded || busy || !isStale(readAt)) return;
  reload({ force: true, fetcher }).then(() => {
    refreshVersion += 1;
    refreshListeners.forEach((listener) => listener());
  }, () => {});
}

/**
 * Keep the caches fresh: call every refresher on each tick (CHECK_MS), on each
 * focus / visibility change, and once at start (a layout remount on navigation
 * restarts the tick), for a visible tab only. `fetcher` is AuthContext's
 * fetchWithAuth (401 -> refresh -> retry). Returns the cleanup.
 */
export function watchCacheFreshness(refreshers, fetcher) {
  const check = () => {
    if (document.visibilityState !== 'visible') return;
    refreshers.forEach((refresh) => refresh(fetcher));
  };
  const timer = setInterval(check, CHECK_MS);
  window.addEventListener('focus', check);
  document.addEventListener('visibilitychange', check);
  check();
  return () => {
    clearInterval(timer);
    window.removeEventListener('focus', check);
    document.removeEventListener('visibilitychange', check);
  };
}
